import { Table } from 'antd'
import React from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirebase, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import { withHandlers } from 'recompose'
import { compose } from 'redux'

import { Category, CategoryId } from '../../schemas/Category'
import { TenantId } from '../../schemas/Tenant'

import { SHOW_NOTIFICATION } from '../../constants/ActionTypes'
import { ERROR, SUCCESS } from '../../constants/notifications'
import { sortCategoriesByDefaultOrder } from '../../helpers/compare'
import { fsCollections, makeInBatchWithFirebase } from '../../helpers/firestore'
import routes from '../../helpers/routes'
import { defaultLanguage } from '../../shared/languages'
import { DeleteButtonWithConfirmation } from '../ActionWithConfirmation'
import authorize from '../higher-order/authorize'

import './CategoriesList.css'

type RemoveCategoryProps = {
  removeCategoryAction(): void
}

const RemoveCategory: React.FunctionComponent<RemoveCategoryProps> = ({ removeCategoryAction }) => (
  <DeleteButtonWithConfirmation
    title="Are you sure to remove this category?"
    popupPlacement="left"
    onConfirmAction={removeCategoryAction}
  />
)

const columnConfig = (
  tenant: TenantId,
  language: string,
  removeCategory: (categoryId: CategoryId) => void,
) => [
  {
    title: 'Name',
    key: `name:${language}`,
    render: (text, record) => (
      <Link to={routes.editCategory(tenant, record.id)}>{record[`name:${language}`]}</Link>
    ),
  },
  {
    title: 'Level Filter',
    key: 'level',
    dataIndex: 'level',
  },
  {
    title: 'Gender Filter',
    key: `gender`,
    dataIndex: 'gender',
  },
  {
    title: 'Age Filter',
    key: 'age',
    render: (text, record) => {
      const minAge = record.minAge
      const maxAge = record.maxAge

      if (minAge && maxAge) {
        return `${minAge} - ${maxAge}`
      }
      if (minAge) {
        return `≥ ${minAge}`
      }
      if (maxAge) {
        return `≤ ${maxAge}`
      }

      return undefined
    },
  },
  {
    key: 'action',
    width: '2em',
    render: (text, record) => {
      const removeCategoryAction = () => removeCategory(record.id)

      return !record.main && <RemoveCategory removeCategoryAction={removeCategoryAction} />
    },
  },
]

type CategoriesListProps = {
  tenant: TenantId
  globalCategories: Category[]
  removeCategory(categoryId: CategoryId): void
}

const CategoriesList: React.FunctionComponent<CategoriesListProps> = ({
  tenant,
  globalCategories,
  removeCategory,
}) => {
  // TODO take from locale/config
  const isLoading = !isLoaded(globalCategories)

  const tournamentCategoriesSorted = sortCategoriesByDefaultOrder(globalCategories || [])

  return (
    <div className="GlobalCategories">
      <Table
        dataSource={tournamentCategoriesSorted}
        columns={columnConfig(tenant, defaultLanguage, removeCategory)}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={false}
      />
    </div>
  )
}

export default compose<FIXME>(
  authorize('admin'),
  withFirebase,
  withFirestore,
  withRouter,
  firestoreConnect<FIXME>(({ tenant }) => [{ collection: fsCollections.globalCategories(tenant) }]),
  connect(({ firestore }: FIXME, { tenant }: FIXME) => ({
    globalCategories: firestore.ordered[fsCollections.globalCategories(tenant)],
  })),
  withHandlers({
    deleteSuccess: ({ dispatch, history, tenant }: FIXME) => (leaveEdit) => {
      dispatch({ type: SHOW_NOTIFICATION, message: 'Category deleted', kind: SUCCESS })
      if (leaveEdit) {
        history.push(routes.manageCategories(tenant))
      }
    },
    deleteError: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error('Failed to delete category', err)
      dispatch({ type: SHOW_NOTIFICATION, message: 'Failed to delete category', kind: ERROR })
    },
    inBatchBound: makeInBatchWithFirebase,
  }),
  withHandlers({
    removeCategory: ({ inBatchBound, tenant, match, deleteSuccess, deleteError }) => (categoryId) =>
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(fsCollections.globalCategories(tenant))
        batch.delete(collection.doc(categoryId))
      })
        .then(() => deleteSuccess(match.url === routes.editCategory(tenant, categoryId)))
        .catch(deleteError),
  }),
)(CategoriesList)



// WEBPACK FOOTER //
// ./src/components/categories/CategoriesList.tsx